'use client';

import React, { useState, useCallback } from 'react';
import { BarChart3, TrendingDown, Wheat, Download, FileText, Eye, Building2, ChevronDown, CheckCircle2, Loader2, ArrowUpRight, ArrowDownRight, Printer,  } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type ReportId = 'monthly-sales' | 'mortality-trends' | 'feed-efficiency';
type ExportFormat = 'csv' | 'pdf';

interface ReportDef {
  id: ReportId;
  label: string;
  description: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  category: string;
}

// ─── Report Definitions ───────────────────────────────────────────────────────

const REPORTS: ReportDef[] = [
  {
    id: 'monthly-sales',
    label: 'Monthly Sales Summary',
    description: 'Revenue, volume, and payment status breakdown by month and product type for stakeholder review.',
    icon: BarChart3,
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    category: 'Commercial',
  },
  {
    id: 'mortality-trends',
    label: 'Mortality Trends',
    description: 'Daily and cumulative mortality rates by flock, cause category, and farm over the selected period.',
    icon: TrendingDown,
    iconBg: '#fef2f2',
    iconColor: '#dc2626',
    category: 'Operations',
  },
  {
    id: 'feed-efficiency',
    label: 'Feed Efficiency (FCR)',
    description: 'Feed Conversion Ratio analysis per flock and house — feed consumed vs. weight gain or egg output.',
    icon: Wheat,
    iconBg: '#fffbeb',
    iconColor: '#d97706',
    category: 'Operations',
  },
];

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MONTHLY_SALES_DATA = [
  { month: 'Apr', broilers: 18400000, eggs: 9200000, layers: 4100000, total: 31700000, paid: 28900000, outstanding: 2800000 },
  { month: 'May', broilers: 21500000, eggs: 10800000, layers: 3800000, total: 36100000, paid: 33200000, outstanding: 2900000 },
  { month: 'Jun', broilers: 19800000, eggs: 11400000, layers: 5200000, total: 36400000, paid: 34100000, outstanding: 2300000 },
  { month: 'Jul', broilers: 24100000, eggs: 12600000, layers: 4700000, total: 41400000, paid: 38800000, outstanding: 2600000 },
  { month: 'Aug', broilers: 22700000, eggs: 13100000, layers: 6300000, total: 42100000, paid: 40200000, outstanding: 1900000 },
  { month: 'Sep', broilers: 26300000, eggs: 14200000, layers: 5800000, total: 46300000, paid: 43100000, outstanding: 3200000 },
];

const MONTHLY_SALES_TABLE = [
  { month: 'April 2026', invoices: 34, revenue: 31700000, paid: 28900000, outstanding: 2800000, pct: 91.2 },
  { month: 'May 2026', invoices: 41, revenue: 36100000, paid: 33200000, outstanding: 2900000, pct: 91.9 },
  { month: 'June 2026', invoices: 38, revenue: 36400000, paid: 34100000, outstanding: 2300000, pct: 93.7 },
  { month: 'July 2026', invoices: 47, revenue: 41400000, paid: 38800000, outstanding: 2600000, pct: 93.7 },
  { month: 'August 2026', invoices: 45, revenue: 42100000, paid: 40200000, outstanding: 1900000, pct: 95.5 },
  { month: 'September 2026', invoices: 29, revenue: 46300000, paid: 43100000, outstanding: 3200000, pct: 93.1 },
];

const MORTALITY_TREND_DATA = [
  { week: 'W1 Jul', disease: 42, injury: 11, nutritional: 8, unknown: 6, total: 67 },
  { week: 'W2 Jul', disease: 38, injury: 9, nutritional: 5, unknown: 4, total: 56 },
  { week: 'W3 Jul', disease: 55, injury: 14, nutritional: 7, unknown: 9, total: 85 },
  { week: 'W4 Jul', disease: 31, injury: 8, nutritional: 4, unknown: 3, total: 46 },
  { week: 'W1 Aug', disease: 29, injury: 7, nutritional: 3, unknown: 5, total: 44 },
  { week: 'W2 Aug', disease: 47, injury: 12, nutritional: 6, unknown: 7, total: 72 },
  { week: 'W3 Aug', disease: 33, injury: 9, nutritional: 4, unknown: 4, total: 50 },
  { week: 'W4 Aug', disease: 28, injury: 6, nutritional: 3, unknown: 2, total: 39 },
  { week: 'W1 Sep', disease: 35, injury: 10, nutritional: 5, unknown: 6, total: 56 },
];

const MORTALITY_FLOCK_TABLE = [
  { flock: 'BR-2026-002', type: 'Broiler', opening: 5000, deaths: 386, culled: 12, rate: 7.72, threshold: 8.0, status: 'ok' },
  { flock: 'BR-2026-003', type: 'Broiler', opening: 3500, deaths: 68, culled: 4, rate: 1.94, threshold: 8.0, status: 'ok' },
  { flock: 'LY-2026-001', type: 'Layer', opening: 8000, deaths: 258, culled: 18, rate: 3.23, threshold: 5.0, status: 'ok' },
  { flock: 'LY-2026-002', type: 'Layer', opening: 6500, deaths: 182, culled: 9, rate: 2.80, threshold: 5.0, status: 'ok' },
  { flock: 'BR-2026-001', type: 'Broiler', opening: 4800, deaths: 421, culled: 31, rate: 8.79, threshold: 8.0, status: 'breach' },
];

const FEED_EFFICIENCY_DATA = [
  { week: 'W1 Jul', br002: 1.82, br003: 1.91, ly001: 2.14, ly002: 2.08 },
  { week: 'W2 Jul', br002: 1.79, br003: 1.88, ly001: 2.11, ly002: 2.05 },
  { week: 'W3 Jul', br002: 1.85, br003: 1.94, ly001: 2.18, ly002: 2.12 },
  { week: 'W4 Jul', br002: 1.77, br003: 1.86, ly001: 2.09, ly002: 2.03 },
  { week: 'W1 Aug', br002: 1.74, br003: 1.83, ly001: 2.07, ly002: 2.01 },
  { week: 'W2 Aug', br002: 1.81, br003: 1.90, ly001: 2.13, ly002: 2.07 },
  { week: 'W3 Aug', br002: 1.76, br003: 1.85, ly001: 2.10, ly002: 2.04 },
  { week: 'W4 Aug', br002: 1.72, br003: 1.81, ly001: 2.06, ly002: 2.00 },
  { week: 'W1 Sep', br002: 1.78, br003: 1.87, ly001: 2.12, ly002: 2.06 },
];

const FEED_FCR_TABLE = [
  { flock: 'BR-2026-002', type: 'Broiler', feedConsumedKg: 18420, weightGainKg: 10340, fcr: 1.78, target: 1.80, variance: -0.02, status: 'good' },
  { flock: 'BR-2026-003', type: 'Broiler', feedConsumedKg: 12180, weightGainKg: 6520, fcr: 1.87, target: 1.80, variance: 0.07, status: 'watch' },
  { flock: 'LY-2026-001', type: 'Layer', feedConsumedKg: 24600, weightGainKg: 11600, fcr: 2.12, target: 2.20, variance: -0.08, status: 'good' },
  { flock: 'LY-2026-002', type: 'Layer', feedConsumedKg: 19800, weightGainKg: 9600, fcr: 2.06, target: 2.20, variance: -0.14, status: 'good' },
  { flock: 'BR-2026-001', type: 'Broiler', feedConsumedKg: 16800, weightGainKg: 7900, fcr: 2.13, target: 1.80, variance: 0.33, status: 'poor' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  if (n >= 1_000_000) return `UGX ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `UGX ${(n / 1_000).toFixed(0)}K`;
  return `UGX ${n.toLocaleString()}`;
}

function generateCSV(reportId: ReportId): string {
  if (reportId === 'monthly-sales') {
    const header = 'Month,Invoices,Revenue (UGX),Paid (UGX),Outstanding (UGX),Collection %\n';
    const rows = MONTHLY_SALES_TABLE.map(
      (r) => `${r.month},${r.invoices},${r.revenue},${r.paid},${r.outstanding},${r.pct}`
    ).join('\n');
    return header + rows;
  }
  if (reportId === 'mortality-trends') {
    const header = 'Flock,Type,Opening Count,Deaths,Culled,Mortality Rate %,Threshold %,Status\n';
    const rows = MORTALITY_FLOCK_TABLE.map(
      (r) => `${r.flock},${r.type},${r.opening},${r.deaths},${r.culled},${r.rate},${r.threshold},${r.status}`
    ).join('\n');
    return header + rows;
  }
  // feed-efficiency
  const header = 'Flock,Type,Feed Consumed (kg),Weight Gain (kg),FCR,Target FCR,Variance,Status\n';
  const rows = FEED_FCR_TABLE.map(
    (r) => `${r.flock},${r.type},${r.feedConsumedKg},${r.weightGainKg},${r.fcr},${r.target},${r.variance},${r.status}`
  ).join('\n');
  return header + rows;
}

function downloadCSV(reportId: ReportId, label: string) {
  const csv = generateCSV(reportId);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${label.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadPDF(label: string) {
  // Opens print dialog — browser's built-in PDF export
  const printContent = document.getElementById('report-preview-area');
  if (!printContent) return;
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`
    <html>
      <head>
        <title>${label}</title>
        <style>
          body { font-family: sans-serif; font-size: 12px; padding: 24px; color: #111; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th { background: #f3f4f6; text-align: left; padding: 8px; font-size: 11px; border-bottom: 2px solid #e5e7eb; }
          td { padding: 7px 8px; border-bottom: 1px solid #f3f4f6; font-size: 11px; }
          h2 { font-size: 16px; margin-bottom: 4px; }
          p { color: #6b7280; font-size: 11px; margin-bottom: 16px; }
          .badge-ok { color: #16a34a; font-weight: 600; }
          .badge-breach { color: #dc2626; font-weight: 600; }
          .badge-good { color: #16a34a; font-weight: 600; }
          .badge-watch { color: #d97706; font-weight: 600; }
          .badge-poor { color: #dc2626; font-weight: 600; }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.close();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, trend }: { label: string; value: string; sub?: string; trend?: 'up' | 'down' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && (
        <p className={`text-xs mt-1 flex items-center gap-1 ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-gray-400'}`}>
          {trend === 'up' && <ArrowUpRight size={12} />}
          {trend === 'down' && <ArrowDownRight size={12} />}
          {sub}
        </p>
      )}
    </div>
  );
}

// ─── Report Previews ──────────────────────────────────────────────────────────

function MonthlySalesPreview() {
  const totalRevenue = MONTHLY_SALES_TABLE.reduce((s, r) => s + r.revenue, 0);
  const totalPaid = MONTHLY_SALES_TABLE.reduce((s, r) => s + r.paid, 0);
  const totalOutstanding = MONTHLY_SALES_TABLE.reduce((s, r) => s + r.outstanding, 0);
  const avgCollection = ((totalPaid / totalRevenue) * 100).toFixed(1);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Total Revenue" value={fmt(totalRevenue)} sub="+16.2% vs prior period" trend="up" />
        <SummaryCard label="Amount Collected" value={fmt(totalPaid)} sub={`${avgCollection}% collection rate`} trend="up" />
        <SummaryCard label="Outstanding" value={fmt(totalOutstanding)} sub="Across 6 months" />
        <SummaryCard label="Total Invoices" value="234" sub="Avg 39/month" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">Revenue by Product Type (UGX)</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={MONTHLY_SALES_DATA} barSize={14}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} />
            <Tooltip formatter={(v: number) => [`UGX ${(v / 1_000_000).toFixed(2)}M`, '']} />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="broilers" name="Broilers" fill="#16a34a" radius={[2, 2, 0, 0]} />
            <Bar dataKey="eggs" name="Eggs" fill="#2563eb" radius={[2, 2, 0, 0]} />
            <Bar dataKey="layers" name="Layers" fill="#9333ea" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-700">Monthly Sales Summary</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                {['Month', 'Invoices', 'Revenue', 'Paid', 'Outstanding', 'Collection %'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MONTHLY_SALES_TABLE.map((r, i) => (
                <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5 font-medium text-gray-800">{r.month}</td>
                  <td className="px-4 py-2.5 text-gray-600">{r.invoices}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-800">{fmt(r.revenue)}</td>
                  <td className="px-4 py-2.5 text-green-700 font-medium">{fmt(r.paid)}</td>
                  <td className="px-4 py-2.5 text-amber-600">{fmt(r.outstanding)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.pct >= 95 ? 'bg-green-100 text-green-700' : r.pct >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                      {r.pct}%
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold">
                <td className="px-4 py-2.5 text-gray-800">TOTAL</td>
                <td className="px-4 py-2.5 text-gray-800">234</td>
                <td className="px-4 py-2.5 text-gray-800">{fmt(totalRevenue)}</td>
                <td className="px-4 py-2.5 text-green-700">{fmt(totalPaid)}</td>
                <td className="px-4 py-2.5 text-amber-600">{fmt(totalOutstanding)}</td>
                <td className="px-4 py-2.5 text-blue-700">{avgCollection}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MortalityTrendsPreview() {
  const totalDeaths = MORTALITY_FLOCK_TABLE.reduce((s, r) => s + r.deaths, 0);
  const totalCulled = MORTALITY_FLOCK_TABLE.reduce((s, r) => s + r.culled, 0);
  const breachCount = MORTALITY_FLOCK_TABLE.filter((r) => r.status === 'breach').length;
  const avgRate = (MORTALITY_FLOCK_TABLE.reduce((s, r) => s + r.rate, 0) / MORTALITY_FLOCK_TABLE.length).toFixed(2);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Total Deaths" value={totalDeaths.toLocaleString()} sub="Across all flocks" />
        <SummaryCard label="Total Culled" value={totalCulled.toLocaleString()} sub="Authorised culling" />
        <SummaryCard label="Avg Mortality Rate" value={`${avgRate}%`} sub="Period average" />
        <SummaryCard label="Threshold Breaches" value={`${breachCount} flock${breachCount !== 1 ? 's' : ''}`} sub="Require investigation" trend={breachCount > 0 ? 'down' : undefined} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">Weekly Mortality by Cause</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={MORTALITY_TREND_DATA} barSize={10}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="disease" name="Disease" fill="#dc2626" stackId="a" />
            <Bar dataKey="injury" name="Injury" fill="#f97316" stackId="a" />
            <Bar dataKey="nutritional" name="Nutritional" fill="#eab308" stackId="a" />
            <Bar dataKey="unknown" name="Unknown" fill="#9ca3af" stackId="a" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-700">Flock Mortality Summary</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                {['Flock', 'Type', 'Opening', 'Deaths', 'Culled', 'Mortality %', 'Threshold', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MORTALITY_FLOCK_TABLE.map((r, i) => (
                <tr key={i} className={`border-t border-gray-50 hover:bg-gray-50/50 ${r.status === 'breach' ? 'bg-red-50/40' : ''}`}>
                  <td className="px-4 py-2.5 font-medium text-blue-700">{r.flock}</td>
                  <td className="px-4 py-2.5 text-gray-600">{r.type}</td>
                  <td className="px-4 py-2.5 text-gray-700">{r.opening.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-red-600 font-medium">{r.deaths}</td>
                  <td className="px-4 py-2.5 text-orange-600">{r.culled}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-800">{r.rate}%</td>
                  <td className="px-4 py-2.5 text-gray-500">{r.threshold}%</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.status === 'ok' ? 'Within Limit' : '⚠ Breach'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FeedEfficiencyPreview() {
  const avgFCR = (FEED_FCR_TABLE.reduce((s, r) => s + r.fcr, 0) / FEED_FCR_TABLE.length).toFixed(2);
  const totalFeed = FEED_FCR_TABLE.reduce((s, r) => s + r.feedConsumedKg, 0);
  const goodCount = FEED_FCR_TABLE.filter((r) => r.status === 'good').length;
  const poorCount = FEED_FCR_TABLE.filter((r) => r.status === 'poor').length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Average FCR" value={avgFCR} sub="Lower is better" />
        <SummaryCard label="Total Feed Used" value={`${(totalFeed / 1000).toFixed(1)}T`} sub="Across all flocks" />
        <SummaryCard label="Flocks On Target" value={`${goodCount} / ${FEED_FCR_TABLE.length}`} sub="Meeting FCR target" trend="up" />
        <SummaryCard label="Underperforming" value={`${poorCount} flock${poorCount !== 1 ? 's' : ''}`} sub="Above target FCR" trend={poorCount > 0 ? 'down' : undefined} />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-700 mb-3">Weekly FCR Trend by Flock</p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={FEED_EFFICIENCY_DATA}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} />
            <YAxis domain={[1.5, 2.5]} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="br002" name="BR-2026-002" stroke="#16a34a" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="br003" name="BR-2026-003" stroke="#2563eb" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="ly001" name="LY-2026-001" stroke="#9333ea" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="ly002" name="LY-2026-002" stroke="#d97706" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-700">FCR Analysis by Flock</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                {['Flock', 'Type', 'Feed (kg)', 'Output (kg)', 'FCR', 'Target', 'Variance', 'Rating'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-gray-500 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEED_FCR_TABLE.map((r, i) => (
                <tr key={i} className={`border-t border-gray-50 hover:bg-gray-50/50 ${r.status === 'poor' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-4 py-2.5 font-medium text-blue-700">{r.flock}</td>
                  <td className="px-4 py-2.5 text-gray-600">{r.type}</td>
                  <td className="px-4 py-2.5 text-gray-700">{r.feedConsumedKg.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-gray-700">{r.weightGainKg.toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-bold text-gray-900">{r.fcr}</td>
                  <td className="px-4 py-2.5 text-gray-500">{r.target}</td>
                  <td className={`px-4 py-2.5 font-semibold ${r.variance < 0 ? 'text-green-600' : r.variance > 0.1 ? 'text-red-600' : 'text-amber-600'}`}>
                    {r.variance > 0 ? '+' : ''}{r.variance.toFixed(2)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'good' ? 'bg-green-100 text-green-700' : r.status === 'watch' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {r.status === 'good' ? 'On Target' : r.status === 'watch' ? 'Watch' : 'Poor'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ReportingContent() {
  const [activeReport, setActiveReport] = useState<ReportId>('monthly-sales');
  const [dateFrom, setDateFrom] = useState('2026-04-01');
  const [dateTo, setDateTo] = useState('2026-09-06');
  const [farm, setFarm] = useState('All Farms');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const currentReport = REPORTS.find((r) => r.id === activeReport)!;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  function handleGenerate() {
    setGenerating(true);
    setGenerated(false);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
      showToast(`"${currentReport.label}" generated successfully.`);
    }, 1000);
  }

  function handleCSV() {
    downloadCSV(activeReport, currentReport.label);
    showToast('CSV file downloaded.');
  }

  function handlePDF() {
    downloadPDF(currentReport.label);
  }

  return (
    <div className="p-6 space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm animate-fade-in">
          <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stakeholder Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Pre-built queries for monthly sales, mortality trends, and feed efficiency.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCSV}
            disabled={!generated}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-40"
          >
            <Download size={13} />
            Export CSV
          </button>
          <button
            onClick={handlePDF}
            disabled={!generated}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors disabled:opacity-40"
          >
            <Printer size={13} />
            Export PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Panel — Report Selector + Filters */}
        <div className="lg:col-span-1 space-y-4">
          {/* Report Selector */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 space-y-1.5">
            <p className="text-xs font-semibold text-gray-500 px-1 pb-1">Pre-built Queries</p>
            {REPORTS.map((r) => {
              const Icon = r.icon;
              const isActive = activeReport === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => { setActiveReport(r.id); setGenerated(false); }}
                  className={`w-full text-left p-3 rounded-xl border transition-all duration-150 ${isActive ? 'border-green-200 bg-green-50' : 'border-transparent hover:bg-gray-50'}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: r.iconBg }}>
                      <Icon size={14} style={{ color: r.iconColor }} />
                    </div>
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold leading-tight ${isActive ? 'text-green-800' : 'text-gray-800'}`}>{r.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-tight line-clamp-2">{r.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500">Report Parameters</p>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setGenerated(false); }}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setGenerated(false); }}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Farm / Branch</label>
              <div className="relative">
                <Building2 size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={farm}
                  onChange={(e) => { setFarm(e.target.value); setGenerated(false); }}
                  className="w-full text-xs border border-gray-200 rounded-lg pl-8 pr-8 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 appearance-none"
                >
                  <option>All Farms</option>
                  <option>Idudi Poultry Farm</option>
                  <option>Kampala Demo Farm</option>
                  <option>Jinja Branch</option>
                </select>
                <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white text-xs font-bold rounded-xl hover:bg-green-700 transition-colors disabled:opacity-60"
            >
              {generating ? (
                <><Loader2 size={13} className="animate-spin" /> Generating...</>
              ) : (
                <><Eye size={13} /> Generate Report</>
              )}
            </button>
          </div>
        </div>

        {/* Right Panel — Report Preview */}
        <div className="lg:col-span-3">
          {!generated && !generating && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center min-h-[400px] gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                <FileText size={24} className="text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-500">Configure parameters and click Generate</p>
              <p className="text-xs text-gray-400">Select a report, set the date range, and generate.</p>
            </div>
          )}

          {generating && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center min-h-[400px] gap-3">
              <Loader2 size={28} className="text-green-600 animate-spin" />
              <p className="text-sm font-semibold text-gray-600">Generating {currentReport.label}…</p>
            </div>
          )}

          {generated && !generating && (
            <div className="space-y-4">
              {/* Report Header */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">{currentReport.category}</span>
                    <span className="text-xs text-gray-400">Y77 Poultry Management System</span>
                  </div>
                  <h2 className="text-base font-bold text-gray-900">{currentReport.label}</h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Period: {dateFrom} → {dateTo} · Farm: {farm}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleCSV}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <Download size={12} />
                    CSV
                  </button>
                  <button
                    onClick={handlePDF}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Printer size={12} />
                    PDF
                  </button>
                </div>
              </div>

              {/* Report Body */}
              <div id="report-preview-area">
                {activeReport === 'monthly-sales' && <MonthlySalesPreview />}
                {activeReport === 'mortality-trends' && <MortalityTrendsPreview />}
                {activeReport === 'feed-efficiency' && <FeedEfficiencyPreview />}
              </div>

              <p className="text-xs text-gray-400 text-center pb-2">
                Generated by Y77 Poultry Management System · {new Date().toLocaleDateString('en-GB')} · {farm}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
